import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

/**
 * Privacy Policy — DRAFT.
 * Plain-language version that covers the substantive requirements
 * (what we collect, why, who sees it, how to delete) so the App Store
 * submission has a real page to link. NEEDS COUNSEL REVIEW before public
 * launch / paid tier — flagged in the page itself.
 */
const Privacy = () => (
  <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
    <div className="max-w-3xl mx-auto px-6">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-enf-text-muted hover:text-enf-role-info transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Enferno
      </Link>

      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-enf-role-info font-bold mb-3">
        <Shield className="w-3.5 h-3.5" /> Privacy
      </div>
      <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-none mb-3">Privacy Policy</h1>
      <p className="text-enf-text-muted text-sm mb-2">Effective date: pending publication · Draft v0.1 — pending counsel review</p>
      <p className="text-enf-text-secondary text-base leading-relaxed mb-10 max-w-2xl">
        Enferno is built by <strong className="text-enf-text-primary">IronFire Technologies, LLC</strong>, a subsidiary of
        Ironbridge Ventures, LLC. We take your training data seriously because most of it is health-adjacent —
        injuries, pain levels, body measurements, what you eat. Here's how we handle it in plain English.
      </p>

      <Section title="What we collect">
        <ul className="list-disc pl-5 space-y-1.5 text-enf-text-secondary text-sm">
          <li><strong className="text-enf-text-primary">Account info</strong> — email and a securely hashed password (we never see the original).</li>
          <li><strong className="text-enf-text-primary">Your profile</strong> — age, sex, height, weight, injuries, equipment, training preferences, recovery activities, martial arts ranks, and any pain or wellbeing check-ins you log.</li>
          <li><strong className="text-enf-text-primary">Training data</strong> — workouts, sets, weights, sessions, GPS-anchored outdoor activities (if you opt in), and AI coach conversations.</li>
          <li><strong className="text-enf-text-primary">Usage data</strong> — how often you sign in, AI requests made, and minimal device info (browser, OS) for debugging.</li>
        </ul>
      </Section>

      <Section title="What we don't collect">
        <ul className="list-disc pl-5 space-y-1.5 text-enf-text-secondary text-sm">
          <li>Your real-time location outside of the activities you explicitly log.</li>
          <li>Your contacts, photos, microphone, or anything else not directly tied to a feature you used.</li>
          <li>Third-party tracking pixels. No Facebook Pixel, no Google Analytics in the app.</li>
        </ul>
      </Section>

      <Section title="Why we collect it">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          To make Enferno work for you. Workouts adapt to your injuries and pain. Recovery suggestions adapt to your
          recent volume. AI coaching uses your profile to give you specific answers. None of this is targeting or ads.
        </p>
      </Section>

      <Section title="Who sees it">
        <p className="text-enf-text-secondary text-sm leading-relaxed mb-2">
          <strong className="text-enf-text-primary">You.</strong> Always. <strong className="text-enf-text-primary">Us</strong>, when we're
          fixing a bug or you ask for support. <strong className="text-enf-text-primary">No one else by default.</strong>
        </p>
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          When we add payment processing, Stripe will see what they need to charge you (never your password). When
          we add health-platform sync (Apple Health, Google Fit, Whoop, Oura), those platforms only get the data you
          explicitly authorize, and you can revoke at any time.
        </p>
      </Section>

      <Section title="Your rights">
        <p className="text-enf-text-secondary text-sm leading-relaxed mb-2">
          You can <strong className="text-enf-text-primary">export</strong> your data at any time (CSV/JSON downloads via your
          Profile page — coming with the next backend release).
        </p>
        <p className="text-enf-text-secondary text-sm leading-relaxed mb-2">
          You can <strong className="text-enf-text-primary">delete</strong> your account at any time. Account deletion removes
          your profile, workouts, training sessions, pain log, wellbeing log, and AI conversations permanently within
          30 days. Backups roll over within 90 days.
        </p>
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          If you're in California (CCPA), the EU/UK (GDPR), or any other jurisdiction with data-protection law, you
          have the same rights of access, correction, deletion, and portability. Email
          {' '}<a href="mailto:support@enferno.app" className="text-enf-role-info hover:text-enf-text-primary">support@enferno.app</a>{' '}
          and we'll handle it.
        </p>
      </Section>

      <Section title="Security">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          Passwords are hashed with bcrypt (12 rounds). All traffic to and from Enferno is encrypted in transit
          (TLS 1.2+). Tokens are stored in secure platform storage (Keychain on iOS, Keystore on Android, localStorage
          on web). We use brute-force protection on login.
        </p>
      </Section>

      <Section title="Cookies & local storage">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          We use localStorage for your access token (so you stay signed in) and to cache your profile for offline
          access. We don't use cookies for tracking.
        </p>
      </Section>

      <Section title="Children">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          Enferno is not directed at users under 16. If we learn we've collected data from a child under 16 without
          parental consent, we'll delete it.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          We'll update this page when meaningful changes happen, and notify active users by email before changes take
          effect.
        </p>
      </Section>

      <Section title="Contact">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          IronFire Technologies, LLC<br />
          Subsidiary of Ironbridge Ventures, LLC<br />
          <a href="mailto:support@enferno.app" className="text-enf-role-info hover:text-enf-text-primary">support@enferno.app</a>
        </p>
      </Section>

      <div className="mt-10 p-4 bg-enf-role-warning/5 border border-enf-role-warning/30 rounded-md text-xs text-enf-role-warning leading-relaxed">
        <strong>DRAFT NOTICE:</strong> This policy is a plain-language placeholder pending review by counsel.
        Substantive obligations (CCPA, GDPR, HIPAA-adjacent) will be tightened before paid tier launch or App
        Store submission.
      </div>

      <div className="mt-8 text-xs text-enf-text-muted">
        Also see: <Link to="/legal/terms" className="text-enf-role-info hover:text-enf-text-primary">Terms of Service</Link>
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mb-8" data-section={title.toLowerCase().replace(/\s+/g, '-')}>
    <h2 className="font-display text-2xl tracking-wide mb-3 text-enf-text-primary">{title}</h2>
    {children}
  </section>
);

export default Privacy;
